/* eslint-disable */
import {
  Anchor,
  Box,
  Button,
  Divider,
  PasswordInput,
  Text,
  TextInput,
  Select,
  Group,
} from "@mantine/core";

import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import axios from "axios";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ExclamationMark } from "tabler-icons-react";
import { z } from "zod";
import { useAuth } from "api/auth";
import { RegisterFormValues } from "../types";
import AuthLayout from "./AuthLayout";
import { useGetOrganizations } from "api/users/signuporganization";

// Stricter email pattern than Zod's default .email(): requires a proper domain
// with a valid TLD and blocks leading/trailing dots and double dots. Must match
// the server-side EMAIL_REGEX in server/src/lib/zod.ts. Validates format only.
const EMAIL_REGEX =
  /^[a-zA-Z0-9_%+-]+(?:\.[a-zA-Z0-9_%+-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

const registerSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1, { message: "Please enter your first name" }),
    lastName: z
      .string()
      .trim()
      .min(1, { message: "Please enter your last name" }),
    email: z
      .string()
      .email({ message: "Invalid email address" })
      .min(1, { message: "Please enter your email address" })
      .regex(EMAIL_REGEX, { message: "Please enter a valid email address" }),
    password: z
      .string()
      .min(6, { message: "Password must contain at least 6 characters" }),
    employmentType: z.enum(
      ["full-time", "seasonal", "contractor", "probation", "hourly", "term"],
      {
        required_error: "Please select your employment type",
      },
    ),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    // optional: require selecting an organization. Uncomment if you want validation:
    // organizationId: z.string().min(1, { message: "Please select an organization" }),
  })
  .refine(
    (data) =>
      data.employmentType !== "term" || (data.startDate && data.endDate),
    {
      message: "Please enter both start and end dates for term employment",
      path: ["startDate"],
    },
  );

export function RegisterForm() {
  const { register, isRegistering } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading: orgsLoading } = useGetOrganizations();

  // map organizations to mantine select options
  const orgOptions =
    (data?.data ?? []).map((org) => ({
      value: org._id,
      label: org.name ?? "Unnamed organization",
    })) ?? [];

  const form = useForm<RegisterFormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      employmentType: "",
      startDate: "",
      endDate: "",
      organizationId: "",
    },
    validate: zodResolver(registerSchema),
    validateInputOnChange: ["password"],
  });

  const handleSubmit = (
    values: RegisterFormValues & { organizationId?: string },
  ) => {
    register(
      {
        ...values,
        organizationId: values.organizationId ?? "",
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Registration Successful",
            message: "Please login to continue",
            color: "green",
            icon: <Check />,
          });

          navigate("/login", {
            replace: true,
            state: { path: "/attendance-dashboard" },
          });
        },
        onError: (err) => {
          if (axios.isAxiosError(err)) {
            if (err.response?.status === 409) {
              form.setFieldError(
                "email",
                "An account with this email already exists",
              );
              return;
            }
          }

          showNotification({
            title: "Server Error",
            message: "Please try again later",
            color: "red",
            icon: <ExclamationMark />,
          });
        },
      },
    );
  };

  return (
    <AuthLayout title="Sign Up">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          label="First Name"
          mb={20}
          {...form.getInputProps("firstName")}
        />
        <TextInput
          label="Last Name"
          mb={20}
          {...form.getInputProps("lastName")}
        />
        <TextInput label="Email" mb={20} {...form.getInputProps("email")} />
        <PasswordInput
          label="Password"
          description={
            form.errors.password || form.isValid("password")
              ? null
              : "Password must contain at least 6 characters"
          }
          inputWrapperOrder={["label", "input", "description", "error"]}
          styles={{ error: { marginTop: 5 } }}
          {...form.getInputProps("password")}
        />
        {form.isValid("password") && (
          <Text
            size={12}
            color="teal.9"
            mt={5}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Check size={14} />
            <Box ml={5}>Password must contain at least 6 characters</Box>
          </Text>
        )}

        {/* Organization Select */}
        <Box mt={16} mb={8}>
          <Select
            label="Organization"
            placeholder={
              orgsLoading ? "Loading organizations..." : "Select organization"
            }
            data={orgOptions}
            searchable
            nothingFound="No organizations"
            disabled={orgsLoading || orgOptions.length === 0}
            mb={18}
            // bind to form field - if your RegisterFormValues includes `organizationId` this will work
            {...form.getInputProps("organizationId" as any)}
          />
        </Box>

        <Box mt={7}>
          <Select
            label="Employment Type"
            placeholder="Select employment type"
            data={[
              { value: "full-time", label: "Full-Time" },
              { value: "seasonal", label: "Seasonal" },
              { value: "contractor", label: "Contractor" },
              { value: "probation", label: "Probation" },
              { value: "hourly", label: "Hourly" },
              { value: "term", label: "Term" },
            ]}
            mb={20}
            {...form.getInputProps("employmentType")}
          />
        </Box>

        <Group grow>
          <TextInput
            type="date"
            label="Start Date"
            {...form.getInputProps("startDate")}
          />
          <TextInput
            type="date"
            label="End Date"
            placeholder="Select end date"
            {...form.getInputProps("endDate")}
          />
        </Group>
        <Button
          type="submit"
          loading={isRegistering}
          w="100%"
          mt={30}
          size="md"
          color="violet.5"
        >
          Sign Up
        </Button>
      </form>

      <Divider my={30} />
      <Text color="gray.7" align="center">
        Already have an account?
      </Text>
      <Anchor
        component={Link}
        to="/login"
        sx={{ display: "block" }}
        align="center"
      >
        Sign in
      </Anchor>
    </AuthLayout>
  );
}
